import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { graphql } from '~/apollo/generated/v3';
import type { ProjectPartsFragment } from '~/apollo/generated/v3/graphql';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { Panel } from '~/components/common/Panel';
import { Button } from '~/components/ui/button';
import { useZodForm } from '~/utils/forms';
import type { RefetchQueries } from '~/utils/graphql';
import { initialProject, projectSchema } from '~/utils/modules/project';
import { ProjectFormFields } from './ProjectFormFields';

export const UPDATE_PROJECT = graphql(`
  mutation updateProject($id: Int!, $project: ProjectInput!) {
    updateProject(id: $id, project: $project) {
      id
      name
    }
  }
`);

export function UpdateProjectForm({
  project,
  refetchQueries,
}: {
  project: ProjectPartsFragment;
  refetchQueries: RefetchQueries;
}) {
  const [updateProject, { loading, error }] = useMutation(UPDATE_PROJECT, {
    refetchQueries,
  });

  const form = useZodForm({
    schema: projectSchema,
    values: initialProject(project),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await updateProject({
        variables: {
          id: project.id,
          project: values,
        },
      });
      toast.success('Project updated successfully.');
    } catch (err) {
      console.log('Error updating project', err);
      toast.error('There was a problem updating the project.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit}>
        <Panel>
          <Panel.Heading>
            <Panel.Title>Project details</Panel.Title>
          </Panel.Heading>

          <Panel.Body className="space-y-4">
            <ProjectFormFields />
            <HookFormErrors graphQLError={error} />
          </Panel.Body>

          <Panel.Footer className="text-right">
            <Button type="submit" color="primary" loading={loading}>
              Save Details
            </Button>
          </Panel.Footer>
        </Panel>
      </form>
    </FormProvider>
  );
}
